import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { resolveFileUrl } from "../lib/urls";
import { Camera, Video as VideoIcon, MessageSquarePlus, Upload as UploadIcon, X, Check, ShieldCheck, Heart, Clock, Mail, Send, Sparkles } from "lucide-react";
import RSVPForm from "../components/RSVPForm";
import Logo from "../components/Logo";
import HighlightsTeaser from "../components/HighlightsTeaser";

// -----------------------------------------------------------
// Expired gallery screen — shown when the event has closed. Lets guests
// nudge the host to renew via a single-tap CTA that emails the host and
// files a request in the admin queue.
// -----------------------------------------------------------
const ExpiredGalleryScreen = ({ event, slug }) => {
  const [name, setName] = useState(localStorage.getItem("gp_guest_name") || "");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const send = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await axios.post(
        `${API}/public/events/${slug}/ask-renew`,
        { guest_name: name || null, guest_email: email || null, note: note || null },
        { withCredentials: true, headers: { "X-Requested-With": "XMLHttpRequest" } },
      );
      if (r.data?.ok) {
        setSent(true);
        if (name) localStorage.setItem("gp_guest_name", name);
      }
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not send your message. Please try again shortly.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-cream-100 py-16 px-5" data-testid="guest-expired-screen">
      <div className="max-w-lg mx-auto">
        <div className="flex justify-center mb-6"><Logo size="sm" /></div>
        <div className="bg-white rounded-3xl soft-shadow p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 flex items-center justify-center">
            <Clock className="w-7 h-7 text-amber-600" />
          </div>
          <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase mt-5">Gallery closed</p>
          <h1 className="font-serif italic text-navy-700 text-3xl mt-2">{event.name}</h1>
          <p className="text-navy-700/70 text-sm mt-3">
            This gallery has reached the end of its hosting window. Photos are safe with the host,
            but uploads are paused until they reactivate it.
          </p>

          {sent ? (
            <div data-testid="guest-renew-sent" className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
              <Check className="w-6 h-6 text-emerald-600 mx-auto" />
              <div className="font-semibold text-emerald-800 mt-2">Thanks — we let your host know.</div>
              <div className="text-sm text-emerald-700/80 mt-1">They'll get an email nudge. If they reactivate, you can come back and upload again.</div>
            </div>
          ) : (
            <form onSubmit={send} className="mt-6 text-left space-y-3">
              <p className="text-sm text-navy-700/70">
                Want to keep sharing? Send your host a friendly nudge — we'll email them for you.
              </p>
              <div>
                <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Your name</label>
                <input
                  value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="guest-renew-name"
                  placeholder="Diane" maxLength={80}
                  className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Your email (optional)</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-700/40" />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="guest-renew-email"
                    placeholder="diane@example.com"
                    className="mt-1.5 w-full pl-10 pr-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">A note for the host (optional)</label>
                <textarea
                  value={note} onChange={(e) => setNote(e.target.value)}
                  data-testid="guest-renew-note"
                  rows={3} maxLength={500}
                  placeholder="We still have amazing shots from the party to share!"
                  className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm resize-none"
                />
              </div>
              {err && <div className="text-sm bg-red-50 text-red-700 rounded-md px-3 py-2" data-testid="guest-renew-err">{err}</div>}
              <button
                type="submit" disabled={busy}
                data-testid="guest-renew-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-coral-300 text-white px-5 py-3 rounded-full font-semibold hover:bg-coral-400 disabled:opacity-60"
              >
                <Send className="w-4 h-4" /> {busy ? "Sending…" : "Ask host to renew"}
              </button>
            </form>
          )}
        </div>
        <p className="text-xs text-navy-700/50 text-center mt-6">
          Powered by <a href="/" className="text-coral-300 hover:underline">GalerieCop</a>
        </p>
      </div>
    </div>
  );
};

const GuestGalleryPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("gallery");
  const [name, setName] = useState(localStorage.getItem("gp_guest_name") || "");
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const [reactions, setReactions] = useState({}); // { [upload_id]: { liked, count } }

  const loadReactions = async (uploadList) => {
    const items = (uploadList || uploads).filter(u => u.kind !== "message").slice(0, 60);
    const results = await Promise.all(items.map(u =>
      axios.get(`${API}/public/events/${slug}/uploads/${u.id}/reactions`, { withCredentials: true })
        .then(r => [u.id, r.data]).catch(() => [u.id, { liked: false, count: 0 }])
    ));
    setReactions(prev => ({ ...prev, ...Object.fromEntries(results) }));
  };

  const toggleLike = async (uploadId) => {
    try {
      const r = await axios.post(
        `${API}/public/events/${slug}/uploads/${uploadId}/reactions/toggle`,
        {},
        { withCredentials: true }
      );
      setReactions(prev => ({ ...prev, [uploadId]: r.data }));
    } catch (err) {
      console.debug("[gallery] toggle reaction failed:", err?.message);
    }
  };

  const load = async () => {
    try {
      const [e, u] = await Promise.all([
        axios.get(`${API}/public/events/${slug}`),
        axios.get(`${API}/public/events/${slug}/uploads`)
      ]);
      setEvent(e.data); setUploads(u.data);
      // Prefetch reactions for gallery items (fire-and-forget)
      loadReactions(u.data);
    } catch { setErr("Event not found"); }
  };
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const [uploadProgress, setUploadProgress] = useState(null); // {done, total, failed}
  const upload = async (files) => {
    if (!name.trim()) { alert("Please enter your name first"); return; }
    if (!files || files.length === 0) return;
    localStorage.setItem("gp_guest_name", name);
    setUploading(true); setErr("");

    const list = Array.from(files);
    const total = list.length;
    setUploadProgress({ done: 0, total, failed: 0 });
    const failures = [];

    // Sequential + independent — one bad file (too large, unsupported format)
    // must NOT abort the rest of the batch. This is the whole point of
    // "multiple pictures at once" — 8 of 10 succeeding is far better than 0.
    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      const isVideo = file.type.startsWith("video");
      const kind = isVideo ? "video" : "photo";
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("uploader_name", name);
      fd.append("file", file);
      try {
        await axios.post(`${API}/public/events/${slug}/uploads`, fd);
      } catch (e2) {
        const detail = e2?.response?.data?.detail;
        failures.push(`${file.name}: ${typeof detail === "string" ? detail : "upload failed"}`);
      }
      setUploadProgress({ done: i + 1, total, failed: failures.length });
    }

    if (failures.length === 0) {
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } else if (failures.length === total) {
      setErr(`All ${total} uploads failed. ${failures[0]}`);
    } else {
      setErr(`${total - failures.length} of ${total} uploaded. ${failures.length} failed (${failures[0]}${failures.length > 1 ? ` +${failures.length - 1} more` : ""}).`);
    }
    setUploading(false);
    setUploadProgress(null);
    load();
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    localStorage.setItem("gp_guest_name", name);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", "message");
      fd.append("uploader_name", name);
      fd.append("message", message);
      await axios.post(`${API}/public/events/${slug}/uploads`, fd);
      setMessage(""); setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
      load();
    } finally { setUploading(false); }
  };

  const fileUrl = (u) => resolveFileUrl(u.file_url);

  if (err) return <div className="min-h-[80vh] flex items-center justify-center text-navy-700/60">{err}</div>;
  if (!event) return <div className="min-h-[80vh] flex items-center justify-center text-navy-700/60">Loading gallery...</div>;

  // Expired gallery — surface a "Ask host to renew" flow instead of the
  // normal upload UI. Guests still see the header, but uploads/RSVP are gated.
  if (event.is_expired) {
    return <ExpiredGalleryScreen event={event} slug={slug} />;
  }

  const photos = uploads.filter(u => u.kind !== "message");
  const messages = uploads.filter(u => u.kind === "message");
  const accent = event.theme_color || "#e76f51";

  return (
    <div className="min-h-screen bg-cream-100" style={{ "--accent": accent }}>
      {/* Hero */}
      <div className="relative h-[40vh] min-h-[280px] overflow-hidden">
        {/* Corner logo — always visible on guest pages so scanners know
            what tool this is. Kept subtle so it doesn't fight event branding. */}
        <div className="absolute top-4 left-5 z-10">
          <Logo size="sm" tint="white" className="drop-shadow-lg" />
        </div>
        {event.cover_image ? (
          <img src={event.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-coral-200 to-cream-200" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-navy-900/20 to-navy-900/70" />
        <div className="relative h-full max-w-5xl mx-auto px-5 flex flex-col justify-end pb-8 text-white">
          <p className="text-coral-300 uppercase tracking-widest text-xs font-semibold">Welcome</p>
          <h1 className="font-serif italic text-4xl md:text-6xl mt-1">{event.name}</h1>
          {event.date && <div className="text-sm opacity-90 mt-1">{event.date}</div>}
          {event.sponsored && (
            <div data-testid="guest-sponsored-badge" className="mt-3 self-start inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-coral-500 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow">
              <ShieldCheck className="w-3.5 h-3.5" /> Sponsored by GalerieCop
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 -mt-10 relative z-10">
        {/* 15s AI highlights teaser — auto-hidden when there aren't enough photos */}
        <HighlightsTeaser slug={slug} />

        {/* AI Find My Photos CTA — only when host has enabled face grouping */}
        {event.faces_enabled && (
          <a
            href={`/g/${event.slug}/find-me`}
            data-testid="guest-find-me-cta"
            className="mb-4 flex items-center gap-3 bg-gradient-to-r from-coral-300 to-coral-400 text-white rounded-2xl px-5 py-3.5 hover:from-coral-400 hover:to-coral-500 shadow-lg"
          >
            <Sparkles className="w-5 h-5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">Find your photos with a selfie</div>
              <div className="text-xs opacity-90">Snap alone or with friends — AI finds every photo you appear in, together or solo.</div>
            </div>
            <span className="text-xs font-semibold bg-white/20 px-3 py-1 rounded-full">Try it</span>
          </a>
        )}

        {/* Uploader card */}
        <div className="bg-white rounded-3xl soft-shadow p-6 md:p-8">
          <h2 className="font-serif italic text-navy-700 text-2xl mb-4">Add to the gallery</h2>
          <div className="grid md:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Your name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your name" className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {(event.allow_photos || event.allow_videos) && (
                <>
                  <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="guest-file-input" />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="guest-upload-btn"
                    className="bg-coral-300 text-white px-5 py-3 rounded-full font-semibold hover:bg-coral-400 disabled:opacity-60 inline-flex items-center gap-2"
                  >
                    <UploadIcon className="w-4 h-4" />
                    {uploadProgress
                      ? `Uploading ${uploadProgress.done}/${uploadProgress.total}${uploadProgress.failed > 0 ? ` · ${uploadProgress.failed} failed` : ""}`
                      : uploading ? "Uploading..." : "Upload photos / videos"}
                  </button>
                </>
              )}
            </div>
          </div>
          {success && <div className="mt-4 text-emerald-700 bg-emerald-50 rounded-md px-3 py-2 text-sm inline-flex items-center gap-2"><Check className="w-4 h-4" /> Thanks! Added to the gallery.</div>}

          {(event.allow_photos || event.allow_videos) && (
            <div
              data-testid="guest-upload-consent-notice"
              className="mt-4 flex items-start gap-3 bg-cream-100 border border-cream-200 rounded-xl px-4 py-3 text-sm text-navy-700/80"
            >
              <ShieldCheck className="w-5 h-5 text-coral-400 shrink-0 mt-0.5" />
              <p>
                Your photos and videos will be uploaded securely to the private event gallery.
                By uploading, you confirm that you have permission to share this content.
              </p>
            </div>
          )}

          {event.allow_messages && (
            <form onSubmit={sendMessage} className="mt-6 pt-6 border-t border-cream-200">
              <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Leave a guestbook message</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows="3" placeholder="Share a wish, memory or note..." className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" />
              <div className="mt-3 flex justify-end">
                <button type="submit" disabled={uploading || !message.trim() || !name.trim()} className="bg-navy-700 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-navy-800 disabled:opacity-50 inline-flex items-center gap-2"><MessageSquarePlus className="w-4 h-4" /> Send message</button>
              </div>
            </form>
          )}
        </div>

        {/* RSVP block (rendered only when host enabled it) */}
        {event.allow_rsvp && (
          <div className="mt-6">
            <RSVPForm slug={slug} themeColor={accent} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mt-10 border-b border-cream-200">
          {[
            ["gallery", `Gallery (${photos.length})`, Camera],
            ["messages", `Guestbook (${messages.length})`, MessageSquarePlus]
          ].map(([k, lbl, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-3 text-sm font-semibold border-b-2 inline-flex items-center gap-2 ${tab === k ? "border-coral-300 text-coral-500" : "border-transparent text-navy-700/60 hover:text-navy-700"}`}><Icon className="w-4 h-4" /> {lbl}</button>
          ))}
        </div>

        <div className="py-8">
          {tab === "gallery" && (
            photos.length === 0 ? (
              <div className="text-center py-16 text-navy-700/60">Be the first to add a photo or video!</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {photos.map(u => {
                  const rx = reactions[u.id] || { liked: false, count: 0 };
                  return (
                    <div key={u.id} className="relative group">
                      <button onClick={() => setLightbox(u)} className="relative aspect-square rounded-xl overflow-hidden bg-cream-200 group block w-full">
                        {u.kind === "photo" ? (
                          <img src={fileUrl(u)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <>
                            <video src={fileUrl(u)} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 flex items-center justify-center"><div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center"><VideoIcon className="w-5 h-5 text-coral-300" /></div></div>
                          </>
                        )}
                        <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/60 to-transparent text-white text-[11px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">{u.uploader_name}</div>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleLike(u.id); }}
                        data-testid={`upload-like-btn-${u.id}`}
                        className={`absolute bottom-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold backdrop-blur transition-colors ${rx.liked ? "bg-white text-red-500" : "bg-black/40 text-white hover:bg-black/60"}`}
                        aria-label={rx.liked ? "Unlike" : "Like"}
                      >
                        <Heart className={`w-3.5 h-3.5 ${rx.liked ? "fill-red-500" : ""}`} />
                        <span data-testid={`upload-like-count-${u.id}`}>{rx.count}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )
          )}
          {tab === "messages" && (
            messages.length === 0 ? (
              <div className="text-center py-16 text-navy-700/60">No messages yet. Be the first to share!</div>
            ) : (
              <div className="space-y-3 max-w-2xl mx-auto">
                {messages.map(m => (
                  <div key={m.id} className="bg-white rounded-2xl p-5 soft-shadow flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-coral-300/20 text-coral-300 flex items-center justify-center font-bold">{(m.uploader_name || "A")[0].toUpperCase()}</div>
                    <div className="flex-1">
                      <div className="font-semibold text-navy-700">{m.uploader_name}</div>
                      <p className="mt-1 text-navy-700/80 whitespace-pre-line">{m.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <button className="absolute top-4 right-4 text-white p-2"><X className="w-6 h-6" /></button>
          {lightbox.kind === "photo" ? (
            <img src={fileUrl(lightbox)} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
          ) : (
            <video src={fileUrl(lightbox)} controls autoPlay className="max-w-full max-h-[90vh] rounded-lg" />
          )}
        </div>
      )}
    </div>
  );
};

export default GuestGalleryPage;
